import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  getForecastStore,
  triggerSnapshot,
  getDatasetRefreshes,
  triggerDatasetRefresh,
  getPipelineRuns,
  triggerPipelineRun,
  ForecastStoreData,
  ForecastSnapshotPoint,
  MonthAccuracyRecord,
  DatasetRefreshInfo,
  PipelineInfo,
} from "../utils/PowerBIApiCall";
import { Loading } from "../ui-components/Loading";
import { formatCurrency } from "../utils/format";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatMonth(month: string): string {
  const [year, mo] = month.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(mo, 10) - 1]} ${year}`;
}

function getAccuracyColor(pct: number): string {
  if (pct >= 97) return "#2DA770";
  if (pct >= 93) return "#f59e0b";
  return "#EF4444";
}

function getStatusColor(status: string): string {
  switch (status) {
    case "Completed":
    case "Succeeded":
      return "text-green-600 bg-green-50";
    case "Failed":
      return "text-red-600 bg-red-50";
    case "InProgress":
    case "Queued":
    case "Unknown":
      return "text-yellow-600 bg-yellow-50";
    case "Cancelled":
    case "Cancelling":
      return "text-gray-500 bg-gray-50";
    case "Disabled":
      return "text-gray-400 bg-gray-50";
    default:
      return "text-gray-600 bg-gray-50";
  }
}

function formatDuration(startTime: string, endTime?: string): string {
  if (!endTime) return "In progress";
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const diffMs = end - start;
  if (diffMs < 1000) return "<1s";
  if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
  const mins = Math.floor(diffMs / 60000);
  const secs = Math.round((diffMs % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatDurationMs(ms?: number): string {
  if (ms == null) return "—";
  if (ms < 1000) return "<1s";
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

type AdminTab = "refreshes" | "pipelines" | "snapshots" | "accuracy";

function AdminContent() {
  const [store, setStore] = useState<ForecastStoreData | null>(null);
  const [refreshData, setRefreshData] = useState<DatasetRefreshInfo[] | null>(
    null,
  );
  const [pipelineData, setPipelineData] = useState<PipelineInfo[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>("refreshes");
  const [snapshotRunning, setSnapshotRunning] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState<string | null>(null);
  const [refreshingDataset, setRefreshingDataset] = useState<string | null>(
    null,
  );
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [triggeringPipeline, setTriggeringPipeline] = useState<string | null>(
    null,
  );
  const [pipelineMsg, setPipelineMsg] = useState<string | null>(null);
  const [hiddenPipelines, setHiddenPipelines] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem("admin:hiddenPipelines");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showPipelineFilter, setShowPipelineFilter] = useState(false);
  const [showInactive, setShowInactive] = useState(() => {
    try {
      return localStorage.getItem("admin:showInactivePipelines") === "true";
    } catch {
      return false;
    }
  });

  const INACTIVE_HOURS = 48;

  function isPipelineActive(pipeline: PipelineInfo): boolean {
    if (pipeline.lastRuns.length === 0) return false;
    const latestRun = pipeline.lastRuns[0];
    const latestTime = latestRun.runEnd || latestRun.runStart;
    const cutoff = Date.now() - INACTIVE_HOURS * 3600 * 1000;
    return new Date(latestTime).getTime() > cutoff;
  }

  function togglePipeline(key: string) {
    setHiddenPipelines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem(
        "admin:hiddenPipelines",
        JSON.stringify(Array.from(next)),
      );
      return next;
    });
  }

  const [refreshingPipelines, setRefreshingPipelines] = useState(false);
  const [lastPipelineRefresh, setLastPipelineRefresh] = useState<Date | null>(
    null,
  );
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshPipelines = useCallback(async (force: boolean = true) => {
    setRefreshingPipelines(true);
    try {
      const updated = await getPipelineRuns(force);
      setPipelineData(updated);
      setLastPipelineRefresh(new Date());
    } catch {
      // Silently ignore — existing data stays
    } finally {
      setRefreshingPipelines(false);
    }
  }, []);

  // Auto-poll every 15s when any pipeline is active (InProgress/Queued)
  const hasActivePipeline = pipelineData?.some((p) =>
    p.lastRuns.some((r) => r.status === "InProgress" || r.status === "Queued"),
  );

  useEffect(() => {
    if (hasActivePipeline && activeTab === "pipelines") {
      pollTimerRef.current = setInterval(() => refreshPipelines(true), 15_000);
      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      };
    }
    // Clear any existing timer when conditions change
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [hasActivePipeline, activeTab, refreshPipelines]);

  useEffect(() => {
    async function load() {
      try {
        const [storeData, refreshes, pipelines] = await Promise.all([
          getForecastStore(),
          getDatasetRefreshes(),
          getPipelineRuns(),
        ]);
        setStore(storeData);
        setRefreshData(refreshes);
        setPipelineData(pipelines);
        setLastPipelineRefresh(new Date());
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load admin data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (!store) return null;

  // Flatten snapshots across all portcos, sorted newest first
  const allSnapshots: (ForecastSnapshotPoint & { portcoId: string })[] = [];
  for (const [portcoId, snaps] of Object.entries(store.snapshots)) {
    for (const snap of snaps) {
      allSnapshots.push({ ...snap, portcoId });
    }
  }
  allSnapshots.sort((a, b) => b.date.localeCompare(a.date));

  // Flatten accuracy records across all portcos, sorted newest first
  const allAccuracy: MonthAccuracyRecord[] = [];
  for (const records of Object.values(store.accuracy)) {
    allAccuracy.push(...records);
  }
  allAccuracy.sort((a, b) => b.month.localeCompare(a.month));

  // Group pipelines by system, respecting visibility + activity filters
  const allAdfPipelines = pipelineData?.filter((p) => p.system === "adf") || [];
  const allSynapsePipelines =
    pipelineData?.filter((p) => p.system === "synapse") || [];
  const inactiveCount =
    allAdfPipelines.filter((p) => !isPipelineActive(p)).length +
    allSynapsePipelines.filter((p) => !isPipelineActive(p)).length;
  const adfPipelines = allAdfPipelines.filter(
    (p) =>
      !hiddenPipelines.has(`adf:${p.name}`) &&
      (showInactive || isPipelineActive(p)),
  );
  const synapsePipelines = allSynapsePipelines.filter(
    (p) =>
      !hiddenPipelines.has(`synapse:${p.name}`) &&
      (showInactive || isPipelineActive(p)),
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
          <p className="text-sm text-gray-500 mt-1">
            Power BI datasets, pipelines, forecast snapshots, and accuracy
            tracking
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            <Link
              to="/admin/email-schedules"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-wmp-500 hover:text-wmp-600"
            >
              Manage Email Schedules &rarr;
            </Link>
            <Link
              to="/admin/email-history"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-wmp-500 hover:text-wmp-600"
            >
              Email History &rarr;
            </Link>
            <Link
              to="/admin/email-templates"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-wmp-500 hover:text-wmp-600"
            >
              Email Templates &rarr;
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {snapshotMsg && (
            <span className="text-xs text-green-600 font-medium">
              {snapshotMsg}
            </span>
          )}
          <button
            disabled={snapshotRunning}
            onClick={async () => {
              setSnapshotRunning(true);
              setSnapshotMsg(null);
              try {
                const result = await triggerSnapshot();
                setStore(result.store);
                setSnapshotMsg("Snapshot recorded");
                setTimeout(() => setSnapshotMsg(null), 5000);
              } catch (e) {
                setSnapshotMsg(
                  e instanceof Error ? e.message : "Snapshot failed",
                );
              } finally {
                setSnapshotRunning(false);
              }
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-wmp-500 hover:bg-wmp-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {snapshotRunning ? "Running..." : "Run Snapshot"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-full sm:w-fit overflow-x-auto">
        <button
          onClick={() => setActiveTab("refreshes")}
          className={`px-2.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeTab === "refreshes"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Dataset Refreshes
        </button>
        <button
          onClick={() => setActiveTab("pipelines")}
          className={`px-2.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeTab === "pipelines"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Pipelines
        </button>
        <button
          onClick={() => setActiveTab("snapshots")}
          className={`px-2.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeTab === "snapshots"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Daily Snapshots ({allSnapshots.length})
        </button>
        <button
          onClick={() => setActiveTab("accuracy")}
          className={`px-2.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeTab === "accuracy"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Monthly Accuracy ({allAccuracy.length})
        </button>
      </div>

      {/* Dataset Refreshes */}
      {activeTab === "refreshes" && (
        <div className="space-y-4">
          {refreshMsg && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700 font-medium">
              {refreshMsg}
            </div>
          )}
          {!refreshData || refreshData.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">
              No dataset refresh data available.
            </div>
          ) : (
            refreshData.map((dataset) => {
              const lastRefresh =
                dataset.refreshes.length > 0 ? dataset.refreshes[0] : null;
              const lastStatus = lastRefresh?.status || "Unknown";
              const lastTime = lastRefresh?.endTime || lastRefresh?.startTime;
              return (
                <div
                  key={dataset.datasetId}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                >
                  {/* Dataset header */}
                  <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                          {dataset.datasetName}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(lastStatus)}`}
                        >
                          {lastStatus}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {dataset.portcoIds.join(", ")}
                        {" \u00b7 "}
                        {lastTime
                          ? `Last refresh: ${timeAgo(lastTime)}`
                          : "Never refreshed"}
                      </p>
                    </div>
                    <button
                      disabled={refreshingDataset === dataset.datasetId}
                      onClick={async () => {
                        setRefreshingDataset(dataset.datasetId);
                        setRefreshMsg(null);
                        try {
                          await triggerDatasetRefresh(
                            dataset.datasetId,
                            dataset.workspaceId,
                          );
                          setRefreshMsg(
                            `Refresh triggered for ${dataset.datasetName}`,
                          );
                          setTimeout(() => setRefreshMsg(null), 5000);
                          setTimeout(async () => {
                            try {
                              const updated = await getDatasetRefreshes();
                              setRefreshData(updated);
                            } catch {
                              // Silently ignore
                            }
                          }, 3000);
                        } catch (e) {
                          setRefreshMsg(
                            e instanceof Error
                              ? e.message
                              : "Refresh trigger failed",
                          );
                        } finally {
                          setRefreshingDataset(null);
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-wmp-500 border border-wmp-500/30 hover:bg-wmp-500/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {refreshingDataset === dataset.datasetId
                        ? "Triggering..."
                        : "Refresh Now"}
                    </button>
                  </div>

                  {/* Refresh history table */}
                  {dataset.refreshes.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-sm">
                      No refresh history available for this dataset.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100 bg-gray-50/50">
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Started
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Ended
                            </th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Duration
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {dataset.refreshes.map((refresh, idx) => (
                            <tr
                              key={`${refresh.id}-${idx}`}
                              className="hover:bg-gray-50/50"
                            >
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(refresh.status)}`}
                                >
                                  {refresh.status}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                                {refresh.refreshType}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                                {formatDateTime(refresh.startTime)}
                              </td>
                              <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                {refresh.endTime
                                  ? formatDateTime(refresh.endTime)
                                  : "\u2014"}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-gray-600 whitespace-nowrap">
                                {formatDuration(
                                  refresh.startTime,
                                  refresh.endTime,
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Pipelines */}
      {activeTab === "pipelines" && (
        <div className="space-y-6">
          {pipelineMsg && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700 font-medium">
              {pipelineMsg}
            </div>
          )}

          {/* Refresh bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                disabled={refreshingPipelines}
                onClick={() => refreshPipelines(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className={`w-3.5 h-3.5 ${refreshingPipelines ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {refreshingPipelines ? "Refreshing..." : "Refresh"}
              </button>
              {lastPipelineRefresh && (
                <span className="text-xs text-gray-400">
                  Updated {timeAgo(lastPipelineRefresh.toISOString())}
                </span>
              )}
            </div>
            {hasActivePipeline && (
              <span className="inline-flex items-center gap-1.5 text-xs text-yellow-600">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
                </span>
                Auto-refreshing every 15s
              </span>
            )}
          </div>

          {/* Pipeline filter panel */}
          {pipelineData && pipelineData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <button
                onClick={() => setShowPipelineFilter((v) => !v)}
                className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filter Pipelines
                  {(hiddenPipelines.size > 0 || inactiveCount > 0) && (
                    <span className="text-xs text-gray-400 font-normal">
                      {hiddenPipelines.size > 0 &&
                        `${hiddenPipelines.size} hidden`}
                      {hiddenPipelines.size > 0 &&
                        !showInactive &&
                        inactiveCount > 0 &&
                        ", "}
                      {!showInactive &&
                        inactiveCount > 0 &&
                        `${inactiveCount} inactive`}
                    </span>
                  )}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${showPipelineFilter ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showPipelineFilter && (
                <div className="px-5 pb-4 border-t border-gray-100 pt-3">
                  {/* Show inactive toggle */}
                  <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={() => {
                        const next = !showInactive;
                        setShowInactive(next);
                        localStorage.setItem(
                          "admin:showInactivePipelines",
                          String(next),
                        );
                      }}
                      className="rounded border-gray-300 text-wmp-500 focus:ring-wmp-500"
                    />
                    Show inactive pipelines
                    {inactiveCount > 0 && (
                      <span className="text-xs text-gray-400">
                        ({inactiveCount} with no runs in {INACTIVE_HOURS}h)
                      </span>
                    )}
                  </label>

                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {allAdfPipelines.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                          Data Factory
                        </p>
                        {allAdfPipelines.map((p) => {
                          const key = `adf:${p.name}`;
                          const isHidden = hiddenPipelines.has(key);
                          const inactive = !isPipelineActive(p);
                          if (inactive && !showInactive) return null;
                          return (
                            <label
                              key={key}
                              className="flex items-center gap-2 py-0.5 cursor-pointer text-sm text-gray-700 hover:text-gray-900"
                            >
                              <input
                                type="checkbox"
                                checked={!isHidden}
                                onChange={() => togglePipeline(key)}
                                className="rounded border-gray-300 text-wmp-500 focus:ring-wmp-500"
                              />
                              <span className={inactive ? "text-gray-400" : ""}>
                                {p.name}
                              </span>
                              {inactive && (
                                <span className="text-xs text-gray-300 italic">
                                  inactive
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {allSynapsePipelines.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                          Synapse Analytics
                        </p>
                        {allSynapsePipelines.map((p) => {
                          const key = `synapse:${p.name}`;
                          const isHidden = hiddenPipelines.has(key);
                          const inactive = !isPipelineActive(p);
                          if (inactive && !showInactive) return null;
                          return (
                            <label
                              key={key}
                              className="flex items-center gap-2 py-0.5 cursor-pointer text-sm text-gray-700 hover:text-gray-900"
                            >
                              <input
                                type="checkbox"
                                checked={!isHidden}
                                onChange={() => togglePipeline(key)}
                                className="rounded border-gray-300 text-wmp-500 focus:ring-wmp-500"
                              />
                              <span className={inactive ? "text-gray-400" : ""}>
                                {p.name}
                              </span>
                              {inactive && (
                                <span className="text-xs text-gray-300 italic">
                                  inactive
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {hiddenPipelines.size > 0 && (
                    <button
                      onClick={() => {
                        setHiddenPipelines(new Set());
                        localStorage.removeItem("admin:hiddenPipelines");
                      }}
                      className="mt-2 text-xs text-wmp-500 hover:text-wmp-600 font-medium"
                    >
                      Show all
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {!pipelineData || pipelineData.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">
              No pipeline data available. Check that ADF_FACTORY_NAME and
              SYNAPSE_WORKSPACE_NAME are configured.
            </div>
          ) : (
            <>
              {/* ADF Section */}
              {adfPipelines.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Data Factory
                  </h2>
                  <div className="space-y-3">
                    {adfPipelines.map((pipeline) => (
                      <PipelineCard
                        key={`adf-${pipeline.name}`}
                        pipeline={pipeline}
                        triggeringPipeline={triggeringPipeline}
                        onTrigger={async () => {
                          const key = `adf:${pipeline.name}`;
                          setTriggeringPipeline(key);
                          setPipelineMsg(null);
                          try {
                            await triggerPipelineRun("adf", pipeline.name);
                            setPipelineMsg(
                              `ADF pipeline "${pipeline.name}" triggered`,
                            );
                            setTimeout(() => setPipelineMsg(null), 5000);
                            setTimeout(async () => {
                              try {
                                const updated = await getPipelineRuns();
                                setPipelineData(updated);
                              } catch {
                                // Silently ignore
                              }
                            }, 3000);
                          } catch (e) {
                            setPipelineMsg(
                              e instanceof Error
                                ? e.message
                                : "Pipeline trigger failed",
                            );
                          } finally {
                            setTriggeringPipeline(null);
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Synapse Section */}
              {synapsePipelines.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Synapse Analytics
                  </h2>
                  <div className="space-y-3">
                    {synapsePipelines.map((pipeline) => (
                      <PipelineCard
                        key={`synapse-${pipeline.name}`}
                        pipeline={pipeline}
                        triggeringPipeline={triggeringPipeline}
                        onTrigger={async () => {
                          const key = `synapse:${pipeline.name}`;
                          setTriggeringPipeline(key);
                          setPipelineMsg(null);
                          try {
                            await triggerPipelineRun("synapse", pipeline.name);
                            setPipelineMsg(
                              `Synapse pipeline "${pipeline.name}" triggered`,
                            );
                            setTimeout(() => setPipelineMsg(null), 5000);
                            setTimeout(async () => {
                              try {
                                const updated = await getPipelineRuns();
                                setPipelineData(updated);
                              } catch {
                                // Silently ignore
                              }
                            }, 3000);
                          } catch (e) {
                            setPipelineMsg(
                              e instanceof Error
                                ? e.message
                                : "Pipeline trigger failed",
                            );
                          } finally {
                            setTriggeringPipeline(null);
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Daily Snapshots Table */}
      {activeTab === "snapshots" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {allSnapshots.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              No snapshots recorded yet. Snapshots are saved daily by the
              scheduler.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Portco
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Forecast
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Low
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      High
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      R&sup2;
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Biz Days
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Daily Sales
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Sales to Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Open Orders
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Budget
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {allSnapshots.map((snap, idx) => (
                    <tr
                      key={`${snap.portcoId}-${snap.date}-${idx}`}
                      className="hover:bg-gray-50/50"
                    >
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                        {formatDate(snap.date)}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                        {snap.portcoId}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                        {formatMonth(snap.forecastMonth)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {formatCurrency(snap.forecastTotal)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                        {formatCurrency(snap.forecastLow)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                        {formatCurrency(snap.forecastHigh)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {snap.rSquared.toFixed(3)}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600">
                        {snap.businessDaysElapsed}/{snap.businessDaysTotal}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {snap.dailySales === undefined
                          ? "\u2014"
                          : formatCurrency(snap.dailySales)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {snap.actualToDate === undefined
                          ? "\u2014"
                          : formatCurrency(snap.actualToDate)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {formatCurrency(snap.openOrdersIncluded)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                        {formatCurrency(snap.budgetFull)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Monthly Accuracy Table */}
      {activeTab === "accuracy" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {allAccuracy.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              No accuracy records yet. Records are computed on days 1-3 of each
              new month.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Portco
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Forecast
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actual
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Accuracy
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Bias
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      In Band
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Computed
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {allAccuracy.map((rec, idx) => (
                    <tr
                      key={`${rec.portcoId}-${rec.month}-${idx}`}
                      className="hover:bg-gray-50/50"
                    >
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                        {formatMonth(rec.month)}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                        {rec.portcoId}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {formatCurrency(rec.lastForecast)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {formatCurrency(rec.actualTotal)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold">
                        <span
                          style={{ color: getAccuracyColor(rec.accuracyPct) }}
                        >
                          {rec.accuracyPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        <span
                          className={
                            rec.errorPct > 0
                              ? "text-amber-600"
                              : "text-blue-600"
                          }
                        >
                          {rec.errorPct > 0 ? "+" : ""}
                          {rec.errorPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {rec.withinBand ? (
                          <span className="inline-block w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs leading-5 font-bold">
                            &#10003;
                          </span>
                        ) : (
                          <span className="inline-block w-5 h-5 rounded-full bg-red-100 text-red-500 text-xs leading-5 font-bold">
                            &#10007;
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                        {formatCurrency(rec.budgetFull)}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                        {formatDate(rec.computedDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Reusable pipeline card showing name, status, last 3 runs, and trigger button. */
function PipelineCard({
  pipeline,
  triggeringPipeline,
  onTrigger,
}: {
  pipeline: PipelineInfo;
  triggeringPipeline: string | null;
  onTrigger: () => void;
}) {
  const lastRun = pipeline.lastRuns.length > 0 ? pipeline.lastRuns[0] : null;
  const lastStatus = lastRun?.status || "Unknown";
  const isRunning = lastStatus === "InProgress" || lastStatus === "Queued";
  const lastTime = lastRun?.runEnd || lastRun?.runStart;
  const triggerKey = `${pipeline.system}:${pipeline.name}`;

  return (
    <div
      className={`bg-white border rounded-xl shadow-sm overflow-hidden ${isRunning ? "border-yellow-300" : "border-gray-200"}`}
    >
      {/* Pipeline header */}
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-all">
              {pipeline.name}
            </h3>
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(lastStatus)}`}
            >
              {isRunning && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
                </span>
              )}
              {lastStatus}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {isRunning
              ? `Running since ${timeAgo(lastRun!.runStart)}`
              : lastTime
                ? `Last run: ${timeAgo(lastTime)}`
                : "Never run"}
          </p>
        </div>
        <button
          disabled={triggeringPipeline === triggerKey}
          onClick={onTrigger}
          className="px-3 py-1.5 text-xs font-medium text-wmp-500 border border-wmp-500/30 hover:bg-wmp-500/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {triggeringPipeline === triggerKey ? "Triggering..." : "Run Now"}
        </button>
      </div>

      {/* Runs table */}
      {pipeline.lastRuns.length === 0 ? (
        <div className="p-6 text-center text-gray-400 text-sm">
          No recent runs found (last 7 days).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Ended
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pipeline.lastRuns.map((run, idx) => (
                <tr key={`${run.runId}-${idx}`} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(run.status)}`}
                    >
                      {run.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                    {formatDateTime(run.runStart)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                    {run.runEnd ? formatDateTime(run.runEnd) : "\u2014"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-600 whitespace-nowrap">
                    {run.durationInMs != null
                      ? formatDurationMs(run.durationInMs)
                      : run.runEnd
                        ? formatDuration(run.runStart, run.runEnd)
                        : "In progress"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function Admin() {
  const { isAdmin, loading } = useIsAdmin();

  if (loading) return <Loading />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return <AdminContent />;
}
